'use client';

import style from './ProfileDetail.module.scss';
import Avatar from '@/components/ui/avatar/Avatar';
import { DEFAULT_AVATAR } from '@/constants/image';
import {
  IconGenderMale,
  IconGenderFemale,
  IconFB,
  IconIG,
  IconYT,
  IconTikTok,
} from '@/components/icons';
import Link from 'next/link';
import { MeService } from '@/libs/services/me';
import { MeProfile } from '@/types/meProfile';
import { useCallback, useEffect, useState } from 'react';
import { showApiError } from '@/libs/helpers/showApiState';
import { TagsService, type MyTag } from '@/libs/services/tag';
import ButtonBottom from '@/components/ui/button/submit/ButtonBottom';
import ShareSection from '@/components/share/ShareSection';
import { useGlobalSkeletonLoading } from '@/hooks/useGlobalSkeletonLoading';
import { useAppDispatch } from '@/store/hook';
import { startGlobalLoading, stopGlobalLoading } from '@/features/ui/uiLoadingSlice';
import { Skeleton } from 'antd';

const ProfileDetail = () => {
  const isLoading = useGlobalSkeletonLoading(1000);
  const dispatch = useAppDispatch();
  const [meInfoData, setMeInfoData] = useState<MeProfile | null>(null);
  const [myTagsData, setMyTagsData] = useState<MyTag[]>([]);
  const [isSharing, setIsSharing] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = origin ? `${origin}/user/${meInfoData?.womo_id}` : '';
  const text = '來WOMO我跟我互動吧😘';
  const socialMedia = [
    {
      name: 'FB',
      icon: <IconFB width={32} />,
      url: meInfoData?.facebook_url,
    },
    {
      name: 'IG',
      icon: <IconIG key={'IG'} width={32} />,
      url: meInfoData?.instagram_url,
    },
    {
      name: 'YT',
      icon: <IconYT key={'YT'} width={32} />,
      url: meInfoData?.youtube_url,
    },
    {
      name: 'TIKTOK',
      icon: <IconTikTok key={'TIKTOK'} width={32} />,
      url: meInfoData?.tik_tok_url,
    },
  ];
  const getMeInfo = useCallback(async () => {
    dispatch(startGlobalLoading());
    try {
      const result = await MeService.getMeInfo();
      setMeInfoData(result);
    } catch (err) {
      showApiError(err, '取得個人資料失敗');
    } finally {
      dispatch(stopGlobalLoading());
    }
  }, [dispatch]);

  const getMyTags = useCallback(async () => {
    dispatch(startGlobalLoading());
    try {
      const result = await TagsService.getMyTags();
      setMyTagsData(result);
    } catch (err) {
      showApiError(err, '取得個人資料失敗');
    } finally {
      dispatch(stopGlobalLoading());
    }
  }, [dispatch]);
  useEffect(() => {
    getMeInfo();
    getMyTags();
  }, [getMeInfo, getMyTags]);

  return (
    <div className="container-fluid mt-10 w-700">
      {!isSharing ? (
        !isLoading ? (
          <div>
            {/* 頭像區 */}
            <section className="d-flex align-items-center">
              <Avatar
                src={
                  typeof meInfoData?.u_m_photo === 'string' ? meInfoData.u_m_photo : DEFAULT_AVATAR
                }
                size={100}
              />
              <div className="ms-3 d-flex flex-column flex-grow-1">
                <h2 className="fs-5 fw-bold">{meInfoData?.name}</h2>
                <div className="fs-sm-1 text-muted mb-2">{meInfoData?.womo_id}</div>
                <div className="d-flex align-items-center fs-sm-2 text-light">
                  <div className=" bg-brand rounded-pill p-2 me-2 d-flex align-items-center">
                    {meInfoData?.gender && (
                      <span>
                        {meInfoData?.gender === 'male' ? (
                          <IconGenderMale width={16} />
                        ) : (
                          <IconGenderFemale width={16} />
                        )}
                      </span>
                    )}
                    {meInfoData?.star_signs && (
                      <span className="ms-1">{meInfoData?.star_signs}</span>
                    )}
                  </div>
                  {meInfoData?.county && (
                    <span className=" bg-brand rounded-pill p-2">{meInfoData?.county}</span>
                  )}
                </div>
              </div>
            </section>

            {/* 興趣區 */}
            <section className="mt-10">
              <h3 className="fs-6 fw-bold mb-3">我感興趣的</h3>
              <div className="d-flex align-items-center fs-sm-1 text-dark fw-bold flex-wrap">
                {myTagsData
                  .filter((tag) => tag.tag_main_name?.includes('我感興趣的'))
                  .map((item, index) => (
                    <span key={index} className={style.tag}>
                      {item.name}
                    </span>
                  ))}
              </div>
            </section>

            {/* 關於我 */}
            <section className="my-10">
              <h3 className="fs-6 fw-bold mb-3">關於我</h3>
              <div className="d-flex align-items-center fs-sm-1 text-dark fw-bold mb-5">
                {socialMedia
                  .filter((item) => Boolean(item.url))
                  .map((item) => (
                    <Link
                      key={item.name}
                      href={item.url || ''}
                      className={style.mediaIcon}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.icon}
                    </Link>
                  ))}
              </div>
              <p className="fs-sm-1 multiLine">{meInfoData?.banner_introduction}</p>
            </section>
          </div>
        ) : (
          <div>
            {/* 頭像區 */}
            <section className="d-flex align-items-center">
              <Skeleton.Avatar active size={100} shape="circle" />
              <div className="ms-3 d-flex flex-column flex-grow-1">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton.Input
                    key={i}
                    active
                    size="large"
                    style={{ width: 180, marginBottom: 8 }}
                  />
                ))}
              </div>
            </section>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="mt-10">
                <Skeleton.Input active size="large" style={{ width: 180, marginBottom: 8 }} />
              </div>
            ))}
          </div>
        )
      ) : (
        <ShareSection url={url} text={text} />
      )}

      <ButtonBottom color="dark" type="button" onClick={() => setIsSharing((prev) => !prev)}>
        {!isSharing ? '分享我的主頁' : '查看我的小檔案'}
      </ButtonBottom>
    </div>
  );
};

export default ProfileDetail;
